import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2, Clock, ArrowLeft, ArrowRight, Check, ChevronDown,
  User, Building2, Lock, Upload, Paperclip, AlertTriangle, ShieldCheck,
} from "lucide-react";
import api from "../api";
import Logo from "../components/Logo";

export default function KYC() {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState(null);
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const nav = useNavigate();

  const [form, setForm] = useState({
    full_name: "", phone_code: "+91", phone_number: "",
    country_code: "IN", country_name: "India",
    house_no: "", street: "", area: "", landmark: "",
    city: "", state: "", zipcode: "",
    account_type: "individual", company_name: "",
    doc_type: "aadhar", doc_number: "", document: null
  });

  useEffect(() => {
    Promise.all([
      api.get("/api/kyc/status"),
      api.get("/api/kyc/countries"),
      api.get("/api/kyc/detect-country")
    ]).then(([s, c, d]) => {
      setStatus(s.data);
      setCountries(c.data.countries);
      // Auto-detect country
      if (d.data && !s.data?.verified) {
        setForm(f => ({
          ...f,
          country_code: d.data.code,
          country_name: d.data.name,
          phone_code: d.data.phone,
          city: d.data.city || "",
          state: d.data.region || "",
          zipcode: d.data.postal || ""
        }));
      }
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const selectCountry = (c) => {
    setForm({
      ...form,
      country_code: c.code,
      country_name: c.name,
      phone_code: c.phone,
      doc_type: c.docs[0]
    });
    setShowCountryDropdown(false);
    setCountrySearch("");
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.phone.includes(countrySearch)
  );

  const validateName = (text, field) => {
    // Block special chars and numbers in city/state/name
    if (/[^a-zA-Z\s.'-]/.test(text)) {
      toast.error(`${field} cannot contain numbers or special characters`);
      return false;
    }
    if (text.length < 2) {
      toast.error(`${field} too short`);
      return false;
    }
    return true;
  };

  const next = () => {
    if (step === 1) {
      if (!form.full_name || !validateName(form.full_name, "Name")) return;
      if (!form.phone_number || form.phone_number.length < 6) {
        toast.error("Valid phone number required"); return;
      }
      if (!form.country_code) {
        toast.error("Select country"); return;
      }
    }
    if (step === 2) {
      if (!form.house_no || !form.street || !form.area) {
        toast.error("Fill House, Street and Area"); return;
      }
      if (!validateName(form.city, "City")) return;
      if (!validateName(form.state, "State")) return;
      if (!form.zipcode || form.zipcode.length < 3) {
        toast.error("Valid zipcode required"); return;
      }
      if (form.account_type === "business" && !form.company_name) {
        toast.error("Company name required"); return;
      }
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!form.doc_number || !form.document) {
      toast.error("Document number and file required"); return;
    }
    setBusy(true);

    const fullPhone = form.phone_code + form.phone_number;
    const fullAddress = `${form.house_no}, ${form.street}, ${form.area}${form.landmark ? ', '+form.landmark : ''}`;

    const fd = new FormData();
    fd.append("full_name", form.full_name);
    fd.append("phone", fullPhone);
    fd.append("country", form.country_code);
    fd.append("address", fullAddress);
    fd.append("city", form.city);
    fd.append("state", form.state);
    fd.append("zipcode", form.zipcode);
    fd.append("account_type", form.account_type);
    if (form.company_name) fd.append("company_name", form.company_name);
    fd.append("doc_type", form.doc_type);
    fd.append("doc_number", form.doc_number);
    fd.append("document", form.document);

    try {
      await api.post("/api/kyc/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("KYC submitted! Review within 24 hours");
      setTimeout(() => nav("/dashboard"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
      setBusy(false);
    }
  };

  if (status?.verified) {
    return (
      <div className="au-root">
        <div style={{...S.container, paddingTop: 80}}>
          <div className="au-glass au-pad-lg">
            <div style={{textAlign:"center"}}>
              <CheckCircle2 size={60} style={{color:"var(--au-success)",marginBottom:14}} />
              <h2 style={{color:"var(--au-success)",fontSize:24,marginBottom:10}}>You're Verified!</h2>
              <p style={{color:"var(--au-text-2)",marginBottom:24}}>Full access unlocked. Happy scanning!</p>
              <Link to="/dashboard" style={{textDecoration:"none"}}>
                <button className="au-pill au-pill-primary">Go to Dashboard <ArrowRight size={18} /></button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status?.status === "pending") {
    return (
      <div className="au-root">
        <div style={{...S.container, paddingTop: 80}}>
          <div className="au-glass au-pad-lg">
            <div style={{textAlign:"center"}}>
              <Clock size={60} style={{color:"var(--au-warn)",marginBottom:14}} />
              <h2 style={{color:"var(--au-warn)",fontSize:24,marginBottom:10}}>Verification In Progress</h2>
              <p style={{color:"var(--au-text-2)",marginBottom:14}}>Your documents are under review.</p>
              <p style={{color:"var(--au-muted)",fontSize:13,marginBottom:24}}>Submitted: {new Date(status.submitted_at).toLocaleDateString()} · Review takes 24-48 hours</p>
              <Link to="/dashboard" style={{textDecoration:"none"}}>
                <button className="au-pill au-pill-glass"><ArrowLeft size={18} /> Back to Dashboard</button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const country = countries.find(c => c.code === form.country_code) || {docs:["passport"]};

  return (
    <div className="au-root">
      <nav style={S.nav}>
        <Link to="/" style={{textDecoration:"none"}}><Logo size="medium" /></Link>
        <Link to="/dashboard" style={{color:"var(--au-muted)",textDecoration:"none",fontSize:13,display:"inline-flex",alignItems:"center",gap:6}}><ArrowLeft size={14} /> Back</Link>
      </nav>

      <div style={S.container}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div className="au-eyebrow" style={S.tag}>STEP {step} OF 3 · KYC VERIFICATION</div>
          <h1 style={S.h1}>Verify Your <span style={{color:"var(--au-accent-ink)"}}>Identity</span></h1>
          <p className="au-lead" style={{marginBottom:32}}>Required for compliance. Takes 5 minutes.</p>
        </div>

        <div style={S.progress}>
          {[1,2,3].map(n => (
            <div key={n} style={{flex:1, display:"flex", alignItems:"center"}}>
              <div style={{...S.dot, ...(step>=n?S.dotActive:{})}}>{step>n?<Check size={16} />:n}</div>
              {n<3 && <div style={{flex:1, height:2, background: step>n?"var(--au-accent)":"var(--au-hair)", margin:"0 8px"}}/>}
            </div>
          ))}
        </div>

        <div className="au-glass au-pad-lg">
          {step === 1 && (
            <>
              <h3 style={S.h3}>Personal Information</h3>

              <Field label="Full Name (as per government ID)" req>
                <input className="au-input" value={form.full_name}
                  onChange={e=>update("full_name", e.target.value.replace(/[^a-zA-Z\s.'-]/g,''))}
                  placeholder="John Doe"/>
              </Field>

              <Field label="Country" req>
                <div style={{position:"relative"}}>
                  <button className="au-input" style={{textAlign:"left", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center"}}
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                    <span>{form.country_name} ({form.phone_code})</span>
                    <ChevronDown size={16} style={{color:"var(--au-muted)"}} />
                  </button>
                  {showCountryDropdown && (
                    <div style={S.dropdown} className="au-glass">
                      <input className="au-input" style={{marginBottom:8}} placeholder="Search country..."
                        value={countrySearch} onChange={e=>setCountrySearch(e.target.value)} autoFocus/>
                      <div style={{maxHeight:240, overflowY:"auto"}}>
                        {filteredCountries.map(c => (
                          <div key={c.code} onClick={()=>selectCountry(c)}
                            style={S.dropdownItem}
                            onMouseEnter={e=>e.currentTarget.style.background="var(--au-glass-strong)"}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{flex:1}}>{c.name}</span>
                            <span className="au-mono" style={{color:"var(--au-muted)",fontSize:12}}>{c.phone}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Phone Number" req>
                <div style={{display:"flex",gap:8}}>
                  <div className="au-input au-mono" style={{width:80, display:"flex", alignItems:"center", justifyContent:"center", color:"var(--au-accent-ink)", fontWeight:700}}>
                    {form.phone_code}
                  </div>
                  <input className="au-input" style={{flex:1}} value={form.phone_number}
                    onChange={e=>update("phone_number", e.target.value.replace(/[^0-9]/g,''))}
                    placeholder="9876543210" maxLength={15}/>
                </div>
              </Field>

              <div style={S.btnRow}>
                <Link to="/dashboard" style={{textDecoration:"none"}}><button className="au-pill au-pill-glass">Cancel</button></Link>
                <button className="au-pill au-pill-primary" onClick={next}>Continue <ArrowRight size={18} /></button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h3 style={S.h3}>Address Information</h3>

              <Field label="Account Type" req>
                <div style={{display:"flex",gap:10}}>
                  {[{k:"individual",l:"Individual",Icon:User},{k:"business",l:"Business",Icon:Building2}].map(t => (
                    <button key={t.k} onClick={()=>update("account_type",t.k)}
                      className={form.account_type===t.k ? "au-pill au-pill-primary" : "au-pill au-pill-glass"}
                      style={{flex:1}}>
                      <t.Icon size={18} /> {t.l}
                    </button>
                  ))}
                </div>
              </Field>

              {form.account_type === "business" && (
                <Field label="Company Name" req>
                  <input className="au-input" value={form.company_name}
                    onChange={e=>update("company_name", e.target.value)}
                    placeholder="Acme Corp Pvt Ltd"/>
                </Field>
              )}

              <Field label="House / Building No." req>
                <input className="au-input" value={form.house_no}
                  onChange={e=>update("house_no", e.target.value)}
                  placeholder="House #123, Apt 4B"/>
              </Field>

              <Field label="Street Name" req>
                <input className="au-input" value={form.street}
                  onChange={e=>update("street", e.target.value)}
                  placeholder="MG Road"/>
              </Field>

              <Field label="Area / Locality" req>
                <input className="au-input" value={form.area}
                  onChange={e=>update("area", e.target.value)}
                  placeholder="Connaught Place"/>
              </Field>

              <Field label="Landmark (Optional)">
                <input className="au-input" value={form.landmark}
                  onChange={e=>update("landmark", e.target.value)}
                  placeholder="Near Metro Station"/>
              </Field>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <Field label="City" req>
                  <input className="au-input" value={form.city}
                    onChange={e=>update("city", e.target.value.replace(/[^a-zA-Z\s.'-]/g,''))}
                    placeholder="Mumbai"/>
                </Field>
                <Field label="State / Province" req>
                  <input className="au-input" value={form.state}
                    onChange={e=>update("state", e.target.value.replace(/[^a-zA-Z\s.'-]/g,''))}
                    placeholder="Maharashtra"/>
                </Field>
              </div>

              <Field label="Zip / Postal Code" req>
                <input className="au-input" value={form.zipcode}
                  onChange={e=>update("zipcode", e.target.value)}
                  placeholder="400001" maxLength={12}/>
              </Field>

              <div style={S.btnRow}>
                <button className="au-pill au-pill-glass" onClick={()=>setStep(1)}><ArrowLeft size={18} /> Back</button>
                <button className="au-pill au-pill-primary" onClick={next}>Continue <ArrowRight size={18} /></button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 style={S.h3}>Document Upload</h3>

              <div style={S.notice}>
                <Lock size={16} style={{flex:"none"}} /> Documents encrypted at rest, auto-deleted after verification (following UIDAI guidelines)
              </div>

              <Field label="Document Type" req>
                <select className="au-input" value={form.doc_type} onChange={e=>update("doc_type", e.target.value)}>
                  {country.docs.map(d => (
                    <option key={d} value={d}>{d.toUpperCase().replace(/_/g," ")}</option>
                  ))}
                </select>
              </Field>

              <Field label="Document Number" req>
                <input className="au-input" value={form.doc_number}
                  onChange={e=>update("doc_number", e.target.value.toUpperCase())}
                  placeholder={form.doc_type === "aadhar" ? "1234 5678 9012" : "Document number"}/>
                <small style={{color:"var(--au-muted)",fontSize:11,marginTop:4,display:"block"}}>
                  Only last 4 digits stored for security
                </small>
              </Field>

              <Field label="Upload Document (JPG/PNG/PDF, max 5MB)" req>
                <div style={S.fileBox}>
                  <input type="file" id="docfile" accept="image/jpeg,image/png,application/pdf"
                    onChange={e => {
                      const f = e.target.files[0];
                      if (!f) return;

                      // Client-side validation
                      const sizeKB = f.size / 1024;
                      if (sizeKB < 30) {
                        alert("File too small (" + Math.round(sizeKB) + "KB). Real documents are usually 50KB+. Please upload an actual ID document, not a thumbnail.");
                        e.target.value = "";
                        return;
                      }
                      if (sizeKB > 5120) {
                        alert("File too large (" + Math.round(sizeKB/1024) + "MB). Max 5MB allowed.");
                        e.target.value = "";
                        return;
                      }

                      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
                      if (!allowedTypes.includes(f.type)) {
                        alert("Only JPG/PNG/PDF files allowed.");
                        e.target.value = "";
                        return;
                      }

                      // For images, additional validation
                      if (f.type.startsWith("image/")) {
                        const img = new Image();
                        img.onload = () => {
                          if (img.width < 400 || img.height < 250) {
                            alert("Image too small (" + img.width + "x" + img.height + "). Min 400x250 required. Make sure you're uploading a clear photo of your ID.");
                            e.target.value = "";
                            return;
                          }
                          update("document", f);
                        };
                        img.onerror = () => {
                          alert("Could not read image file. Please try another image.");
                          e.target.value = "";
                        };
                        img.src = URL.createObjectURL(f);
                      } else {
                        update("document", f);
                      }
                    }}
                    style={{display:"none"}}/>
                  <label htmlFor="docfile" style={S.fileLabel}>
                    {form.document
                      ? <><Paperclip size={16} /> {form.document.name}</>
                      : <><Upload size={16} /> Click to upload document</>}
                  </label>
                </div>
              </Field>

              <div style={S.consent}>
                <div style={{display:"flex",alignItems:"center",gap:6,fontWeight:700,marginBottom:6,color:"var(--au-warn)"}}>
                  <AlertTriangle size={16} /> By submitting, you confirm:
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}><Check size={14} style={{color:"var(--au-success)"}} /> Documents are genuine and belong to you</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}><Check size={14} style={{color:"var(--au-success)"}} /> Information is accurate and current</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}><Check size={14} style={{color:"var(--au-success)"}} /> False documents = permanent ban + legal action</div>
              </div>

              <div style={S.btnRow}>
                <button className="au-pill au-pill-glass" onClick={()=>setStep(2)}><ArrowLeft size={18} /> Back</button>
                <button className="au-pill au-pill-primary" onClick={submit} disabled={busy}>
                  {busy ? "Submitting..." : <>Submit for Review <ArrowRight size={18} /></>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({label, req, children}) {
  return (
    <div style={{marginBottom:16}}>
      <label style={{display:"block",fontSize:12,color:"var(--au-text-2)",marginBottom:6,letterSpacing:1,textTransform:"uppercase"}}>
        {label} {req && <span style={{color:"var(--au-error)"}}>*</span>}
      </label>
      {children}
    </div>
  );
}

const S = {
  nav: {position:"sticky",top:0,zIndex:100,background:"var(--au-glass-strong)",backdropFilter:"var(--au-blur)",WebkitBackdropFilter:"var(--au-blur)",borderBottom:"1px solid var(--au-hair)",padding:"14px 32px",display:"flex",justifyContent:"space-between",alignItems:"center"},
  container: {maxWidth:600,margin:"0 auto",padding:"40px 24px"},
  tag: {marginBottom:14,display:"inline-block",padding:"4px 12px",background:"var(--au-glass-strong)",borderRadius:"var(--au-r-pill)",border:"1px solid var(--au-hair)"},
  h1: {fontSize:32,fontWeight:700,letterSpacing:1,marginBottom:10},
  h3: {fontSize:18,fontWeight:700,marginBottom:20,marginTop:0},
  progress: {display:"flex",alignItems:"center",maxWidth:400,margin:"0 auto 28px"},
  dot: {width:36,height:36,borderRadius:"50%",background:"var(--au-glass)",border:"2px solid var(--au-border)",color:"var(--au-muted)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14},
  dotActive: {background:"var(--au-glass-strong)",borderColor:"var(--au-accent)",color:"var(--au-accent-ink)"},
  fileBox: {border:"2px dashed var(--au-border)",borderRadius:"var(--au-r-input)",padding:20,textAlign:"center",background:"var(--au-glass)"},
  fileLabel: {cursor:"pointer",color:"var(--au-accent-ink)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:8},
  notice: {background:"var(--au-glass-strong)",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-input)",padding:"10px 14px",fontSize:12,color:"var(--au-accent-ink)",marginBottom:18,display:"flex",alignItems:"center",gap:8},
  consent: {background:"var(--au-glass-strong)",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-input)",padding:"12px 14px",fontSize:12,color:"var(--au-text-2)",marginBottom:18,lineHeight:1.9},
  btnRow: {display:"flex",justifyContent:"space-between",gap:10,marginTop:24},
  dropdown: {position:"absolute",top:"100%",left:0,right:0,marginTop:4,padding:8,zIndex:1000},
  dropdownItem: {display:"flex",alignItems:"center",padding:"10px 12px",cursor:"pointer",borderRadius:"var(--au-r-input)",fontSize:14,color:"var(--au-text-2)"}
};
